import React, { useMemo, useState, useCallback } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MuiSwitch, MuiChip, MuiDrawer } from '@ace/ace-ui-web';
import PerfProductSyncSkeletonDataGrid from '../../../../../components/skeletons/performance/ecommerce/PerfProductSyncSkeletonDataGrid';
import styles from '../Ecommerce.module.css';
import pagesStandardStyles from '../../../../Pages.module.css';
import salesByFulfillmentStyles from '../salesbyfulfillment/Salesbyfulfillment.module.css';
import productSyncDetailsStyles from './ProductSyncDetails.module.css';
import {
  formatNumberWithCommas,
  formatLoadDate,
  isValidApiDate,
  renderEmptyValue,
  getAlternatingRowClassName,
  renderPeerGroupRankCell,
  formatNegativeChangeValue,
  isMobileDevice,
} from '../../../../../utils/appUtils';
import { kpiContentMap } from '../../../../../utils/viewInfoUtils';
import lang from '../../../../../languages/en.json';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectIsMultiStoreEnabled } from '../../../../../redux/UserReducer/userSlice';
import {
  selectProductSync,
  selectProductSyncLoading,
} from '../../../../../redux/PerformanceReducer/Ecommerce/productSyncSlice';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import type { ProductSyncDetails as ProductSyncDetailsModel } from '../../../../../models/ProductSyncDetailsModel';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';

// ─── Props interface ─────────────────────────────────────────────────────────

interface ProductSyncDetailsProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: ProductSyncDetailsModel | null;
  /** When true, hides interactive elements (peer group toggle, drawer, view info) */
  isPrintView?: boolean;
}

const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Product Sync Details';

// Row interface for DataGrid
interface GridRow {
  id: number;
  participating: boolean | null;
  multiStoreProductSyncCount?: number;
  sales: number;
  salesPeerGroupRank: string | null;
  salesBenchmark: number | null;
  unitsSold: number;
  unitsSoldPeerGroupRank: string | null;
  unitsSoldBenchmark: number | null;
  crossLinkMatchesMade: number;
  crossLinkMatchesMadePeerGroupRank: string | null;
  crossLinkMatchesWaiting: number;
  crossLinkMatchesWaitingPeerGroupRank: string | null;
}

// Helper function to render currency values with $ and commas
const renderCurrencyCell = (
  params: GridRenderCellParams<GridRow, number | null>,
): JSX.Element | string => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  const formattedValue = formatNumberWithCommas(params.value);
  return `$${formattedValue}`;
};

// Helper function to render number values with commas
const renderNumberCell = (
  params: GridRenderCellParams<GridRow, number | null>,
): JSX.Element | string => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  return formatNumberWithCommas(params.value);
};
// Helper function to render multi-store count
const renderMultiStoreCount = (count: number | undefined): JSX.Element | string | number => {
  if (count == null) {
    return renderEmptyValue(null);
  }
  return count;
};

// Helper function to render participating chip (YES/NO)
const renderParticipatingChip = (value: boolean | null | undefined): JSX.Element | string => {
  if (value == null) {
    return renderEmptyValue(null);
  }

  return value === true ? (
    <MuiChip
      colors="green"
      label={lang.pageContent.yes}
      variant="outlined"
      size="small"
    />
  ) : (
    <MuiChip
      colors="red"
      label={lang.pageContent.no}
      variant="outlined"
      size="small"
    />
  );
};

// Shared benchmark column header — renders an info icon + tooltip alongside
// the "80% Benchmark" text. Layout differs between print and live views so the
// icon stays inside the column width in print.
interface BenchmarkColumnHeaderProps {
  isPrintView: boolean;
}

const BenchmarkColumnHeader: React.FC<BenchmarkColumnHeaderProps> = ({ isPrintView }) =>
  isPrintView ? (
    <div className={pagesStandardStyles.benchmarkHeader}>
      <span className={pagesStandardStyles.benchmarkHeaderRow}>
        80%
        <Tooltip
          title={lang.pageContent.benchmarkPerStoreAveragesNote}
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={6000}>
          <InfoOutlinedIcon
            fontSize="small"
            className={pagesStandardStyles.benchmarkInfoIconHeader}
          />
        </Tooltip>
      </span>
      <span>{lang.pageContent.benchmark}</span>
    </div>
  ) : (
    <div className={pagesStandardStyles.benchmarkHeader}>
      <span>80%</span>
      <span className={pagesStandardStyles.benchmarkHeaderRow}>
        {lang.pageContent.benchmark}
        <Tooltip
          title={lang.pageContent.benchmarkPerStoreAveragesNote}
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={6000}>
          <InfoOutlinedIcon
            fontSize="small"
            className={pagesStandardStyles.benchmarkInfoIconHeader}
          />
        </Tooltip>
      </span>
    </div>
  );

const ProductSyncDetails: React.FC<ProductSyncDetailsProps> = ({
  data: dataProp,
  isPrintView = false,
}) => {
  const [showPeerGroupColumns, setShowPeerGroupColumns] = useState<boolean>(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerContent, setDrawerContent] = useState<{
    title: string;
    content: React.ReactNode;
  }>({
    title: '',
    content: null,
  });

  // Get multi-store mode from Redux
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  const reduxData = useAppSelector(selectProductSync);
  const reduxLoading = useAppSelector(selectProductSyncLoading);
  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  // Use prop data (print view) or Redux data (live view)
  const productSyncData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;

  // Handler for Sales YTD link
  const handleSalesYTDClick = useCallback(() => {
    const url = buildDynamicUrl('WFC', 'YTDS', 'current');
    if (url) {
      handleAndLogClick(`${SECTION_NM} - Sales (YTD)`, { url, urlTarget: '_blank' });
    }
  }, [buildDynamicUrl, handleAndLogClick]);

  // Create single row for the grid
  const rowData = useMemo(() => {
    if (!productSyncData) return [];
    return [
      {
        id: 1,
        participating: productSyncData.isProductSync,
        multiStoreProductSyncCount: productSyncData.multiStoreProductSyncCount,
        sales: productSyncData.sales,
        salesPeerGroupRank: productSyncData.salesPeerGroupRank,
        salesBenchmark: productSyncData.salesBenchmark,
        unitsSold: productSyncData.unitsSold,
        unitsSoldPeerGroupRank: productSyncData.unitsSoldPeerGroupRank,
        unitsSoldBenchmark: productSyncData.unitsSoldBenchmark,
        crossLinkMatchesMade: productSyncData.crossLinkMatchesMade,
        crossLinkMatchesMadePeerGroupRank: productSyncData.crossLinkMatchesMadePeerGroupRank,
        crossLinkMatchesWaiting: productSyncData.crossLinkMatchesWaiting,
        crossLinkMatchesWaitingPeerGroupRank: productSyncData.crossLinkMatchesWaitingPeerGroupRank,
      },
    ];
  }, [productSyncData]);

  // In print view with peer group enabled (11 cols), use smaller minWidth to fit the container.
  const colMinWidth = isPrintView && showPeerGroupColumns ? 85 : 115.2;

  // Define all columns
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'participating',
        headerName: 'Participating',
        minWidth: colMinWidth,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'left',
        renderCell: (params: GridRenderCellParams<GridRow, boolean | null>) => {
          // If multi-store mode is enabled, show the count
          if (isMultiStoreEnabled) {
            return renderMultiStoreCount(params.row.multiStoreProductSyncCount);
          }
          // Otherwise, show YES/NO chips
          return renderParticipatingChip(params.value);
        },
      },
      {
        field: 'sales',
        headerName: 'Sales (YTD)',
        minWidth: colMinWidth,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        renderCell: (params: GridRenderCellParams<GridRow, number>) => {
          const emptyCheck = renderEmptyValue(params.value);
          if (emptyCheck) return emptyCheck;
          const roundedValue = Math.round(params.value); // Round to nearest dollar
          const formattedValue = formatNegativeChangeValue(roundedValue?.toString(), true);
          const salesValue = (
            <span className={productSyncDetailsStyles.salesValue}>{formattedValue}</span>
          );
          return isMobileDevice() || isPrintView ? (
            salesValue
          ) : (
            <a
              onClick={handleSalesYTDClick}
              className={productSyncDetailsStyles.salesLink}>
              {salesValue}
            </a>
          );
        },
      },
      ...(showPeerGroupColumns
        ? [
            {
              field: 'salesPeerGroupRank',
              // Change header text to MS based on multi-store status
              headerName: isMultiStoreEnabled
                ? lang.pageContent.msGroupRank
                : lang.pageContent.peerGroupRank,
              minWidth: colMinWidth,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderPeerGroupRankCell,
            },
            {
              field: 'salesBenchmark',
              headerName: lang.pageContent.percentBenchmark,
              minWidth: colMinWidth,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderCurrencyCell,
              // Custom header to include benchmark explanation tooltip for multi-store mode
              ...(isMultiStoreEnabled && {
                renderHeader: () => <BenchmarkColumnHeader isPrintView={isPrintView} />,
              }),
            },
          ]
        : []),
      {
        field: 'unitsSold',
        headerName: lang.productSyncDetailsContent.unitsSoldYTD,
        minWidth: colMinWidth,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        renderCell: renderNumberCell,
      },
      ...(showPeerGroupColumns
        ? [
            {
              field: 'unitsSoldPeerGroupRank',
              // Change header text to MS based on multi-store status
              headerName: isMultiStoreEnabled
                ? lang.pageContent.msGroupRank
                : lang.pageContent.peerGroupRank,
              minWidth: colMinWidth,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderPeerGroupRankCell,
            },
            {
              field: 'unitsSoldBenchmark',
              headerName: lang.pageContent.percentBenchmark,
              minWidth: colMinWidth,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderNumberCell,
              // Custom header to include benchmark explanation tooltip for multi-store mode
              ...(isMultiStoreEnabled && {
                renderHeader: () => <BenchmarkColumnHeader isPrintView={isPrintView} />,
              }),
            },
          ]
        : []),
      {
        field: 'crossLinkMatchesMade',
        headerName: lang.productSyncDetailsContent.crossLinkMatchesMade,
        minWidth: colMinWidth,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        renderCell: renderNumberCell,
      },
      ...(showPeerGroupColumns
        ? [
            {
              field: 'crossLinkMatchesMadePeerGroupRank',
              // Change header text to MS based on multi-store status
              headerName: isMultiStoreEnabled
                ? lang.pageContent.msGroupRank
                : lang.pageContent.peerGroupRank,
              minWidth: 115.2,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderPeerGroupRankCell,
            },
          ]
        : []),
      {
        field: 'crossLinkMatchesWaiting',
        headerName: lang.productSyncDetailsContent.crossLinkMatchesWaiting,
        minWidth: colMinWidth,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right',
        renderCell: renderNumberCell,
      },
      ...(showPeerGroupColumns
        ? [
            {
              field: 'crossLinkMatchesWaitingPeerGroupRank',
              // Change header text to MS based on multi-store status
              headerName: isMultiStoreEnabled
                ? lang.pageContent.msGroupRank
                : lang.pageContent.peerGroupRank,
              minWidth: colMinWidth,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: 'right' as const,
              renderCell: renderPeerGroupRankCell,
            },
          ]
        : []),
    ],
    [showPeerGroupColumns, isMultiStoreEnabled, handleSalesYTDClick, colMinWidth, isPrintView],
  );

  if (isPrintView && !productSyncData) {
    return null;
  }

  return (
    <div className={styles.sectionContent}>
      {/* Page Header Title Section */}
      <div className={pagesStandardStyles.pageHeader}>
        <div className={`${pagesStandardStyles.headerContainer}`}>
          <div>
            <h2 className={pagesStandardStyles.pageTitle}>{lang.productSyncDetails.title}</h2>
            <p className={pagesStandardStyles.pageSubtitle}>
              {lang.pageContent.datePrefix}{' '}
              {isValidApiDate(productSyncData?.asOfDate)
                ? formatLoadDate(productSyncData?.asOfDate ?? '')
                : 'N/A'}
              {!isPrintView && (
                <span className={pagesStandardStyles.viewInfoLinkWrapper}>
                  <a
                    href="#"
                    className={`${pagesStandardStyles.viewInfoLink} ${productSyncDetailsStyles.viewInfoLink}`}
                    onClick={e => {
                      e.preventDefault();
                      const kpiCode = 'SUPS';
                      const kpiData = kpiContentMap[kpiCode];
                      if (kpiData) {
                        const ContentComponent = kpiData.component;
                        handleAndLogClick(`${SECTION_NM} - View Info`, {
                          callback: () => {
                            setDrawerContent({
                              title: kpiData.title || 'Product Sync',
                              content: <ContentComponent />,
                            });
                            setIsDrawerOpen(true);
                          },
                        });
                      }
                    }}>
                    {lang.pageContent.viewInfo}
                  </a>
                </span>
              )}
            </p>
          </div>
          {!isPrintView && (
            <div
              className={`${pagesStandardStyles.headerActions} ${salesByFulfillmentStyles.headerActions}`}>
              <div
                className={`${pagesStandardStyles.switchWrapper} ${productSyncDetailsStyles.hiddenSwitch}`}>
                <MuiSwitch
                  key={showPeerGroupColumns.toString()}
                  color="primary"
                  defaultChecked={showPeerGroupColumns}
                  label={lang.pageContent.toggleShowPeerGroup}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setShowPeerGroupColumns(event.target.checked);
                  }}
                  size="medium"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DataGrid */}
      <div>
        {isLoading ? (
          <PerfProductSyncSkeletonDataGrid showPeerGroupColumns={showPeerGroupColumns} />
        ) : !productSyncData ? (
          <div className={pagesStandardStyles.errorContent}>
            {lang.pageContent.dataUnavailableError.replace('{section}', 'Product Sync')}
          </div>
        ) : (
          <DataGrid
            rows={rowData}
            columns={columns}
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            hideFooter
            autoHeight
            className={pagesStandardStyles.dataGrid}
            getRowClassName={params =>
              getAlternatingRowClassName(
                params,
                pagesStandardStyles.evenRow,
                pagesStandardStyles.oddRow,
              )
            }
            columnHeaderHeight={52}
            columnBufferPx={1500} // Buffer for horizontal scrolling performance
            sx={{
              // Column header styling - matches getDataGridStyles pattern
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#eeeeee',
                padding: '0 8px',
                borderRight: '1px solid #c9c9c9',
                borderBottom: '1px solid #c9c9c9',
                boxSizing: 'border-box',
              },
              // Participating column header gets left border
              '& .MuiDataGrid-columnHeader[data-field="participating"]': {
                borderLeft: '1px solid #c9c9c9',
              },
              // Product Sync cell (first cell with MuiChip) gets left border and centered alignment
              '& .MuiDataGrid-cell[data-field="participating"]': {
                borderLeft: '1px solid #c9c9c9',
                display: 'flex',
                alignItems: 'center',
              },
              // Target specific class combination for border bottom
              '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
                borderBottom: '1px solid #c9c9c9',
              },
            }}
          />
        )}
      </div>

      {/* Print-only benchmark note below the Product Sync Details grid (multi-store only) */}
      {isPrintView && isMultiStoreEnabled && (
        <div className={pagesStandardStyles.printBenchmarkNote}>
          <InfoOutlinedIcon
            fontSize="small"
            className={pagesStandardStyles.printBenchmarkNoteIcon}
          />
          <span>{lang.pageContent.benchmarkPerStoreAveragesNote}</span>
        </div>
      )}

      {/* MuiDrawer — hidden in print view */}
      {!isPrintView && (
        <MuiDrawer
          headerText={drawerContent.title}
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}>
          {drawerContent.content}
        </MuiDrawer>
      )}
    </div>
  );
};

export default ProductSyncDetails;
